/**
 * @fileoverview Plan Section component for organizer settings.
 * @summary Displays current subscription plan, expiration date, and upgrade options.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/PlanSection
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faCreditCard } from '@fortawesome/free-solid-svg-icons'
import { SubscriptionType, SUBSCRIPTION_PLANS } from '@karaonow/shared'
import { getPlanBadgeLabel, getPlanBadgeColor } from '../../../utils/planUtils'
import type { User } from '../../../store/slices/authSlice'

interface PlanSectionProps {
  user: User | null
  onNavigateToSubscription: () => void
}

/**
 * @summary Plan section component.
 * @param {PlanSectionProps} props - Component props.
 * @returns {React.ReactElement} Plan section.
 */
export function PlanSection({ user, onNavigateToSubscription }: PlanSectionProps) {
  const { t } = useTranslation()

  if (!user) {
    return null
  }

  const currentPlan = (user.subscription?.type as SubscriptionType) || SubscriptionType.FREE
  const planLabel = getPlanBadgeLabel(currentPlan)
  const planColor = getPlanBadgeColor(currentPlan)
  const planConfig = SUBSCRIPTION_PLANS[currentPlan]
  const subscriptionStatus = user.subscription?.status || 'active'
  const isCanceled = subscriptionStatus === 'canceled'
  
  // Calculate FREE events count: total events - purchased events (ONE_SHOT)
  const totalEventsCount = user.subscription && typeof user.subscription === 'object' && 'eventsCount' in user.subscription
    ? (user.subscription.eventsCount as number)
    : 0
  const purchasedEventsCount = user.subscription && typeof user.subscription === 'object' && 'purchasedEvents' in user.subscription
    ? (Array.isArray(user.subscription.purchasedEvents) ? user.subscription.purchasedEvents.length : 0)
    : 0
  const freeEventsCount = totalEventsCount - purchasedEventsCount

  const subscriptionPeriodEnd= new Date(user?.subscription?.subscriptionPeriodEnd as Date).toLocaleDateString(user?.profile?.preferredLanguage, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  console.log('subscriptionPeriodEnd', subscriptionPeriodEnd)
  
  // Format subscription end date if available
  const subscriptionEndDate =
    user.subscription &&
    typeof user.subscription === 'object' &&
    'subscriptionExpiresAt' in user.subscription &&
    user.subscription.subscriptionExpiresAt
      ? new Date(user.subscription.subscriptionExpiresAt as string | number | Date).toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : null

  return (
    <div className="card bg-base-100 dark:bg-base-800 shadow-lg">
      <div className="card-body">
        <div className="flex items-center justify-between mb-4">
          <h2 className="card-title text-xl flex items-center gap-2">
            <FontAwesomeIcon icon={faCreditCard} className="w-5 h-5" />
            {t('organizer.planSection')}
          </h2>
          <div className={`badge badge-lg ${planColor}`}>{planLabel}</div>
        </div>

        {/* Current Plan Details */}
        <div className="space-y-4">
          {/* Plan Name */}
          <div className="p-3 bg-primary/10 dark:bg-primary/20 border border-primary/30 rounded-lg">
            <h3 className="text-sm font-semibold mb-1">{t('subscription.currentPlan')}</h3>
            <h3 className="text-2xl font-bold text-primary">{planConfig.displayName}</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mb-0">
            {/* Events Limit */}
            <div className="p-3 bg-base-200 dark:bg-base-700 rounded-lg">
              <h3 className="text-sm font-semibold mb-1">{t('organizer.eventsLimit')}</h3>
              <h3 className="text-2xl font-bold text-(--color-text-primary)">
                {freeEventsCount} / {planConfig.maxEvents === Infinity ? t('organizer.unlimited') : planConfig.maxEvents}
              </h3>
            </div>

            {/* Subscription Status */}
            <div className="p-3 bg-base-200 dark:bg-base-700 rounded-lg">
              <h3 className="text-sm font-semibold mb-1">{t('organizer.subscriptionStatus')}</h3>
              <h3 className="text-2xl font-bold text-(--color-text-primary)">
                {isCanceled ? (
                  <span className="text-blue-600 dark:text-blue-400">{t('organizer.statusScheduledEnd')}</span>
                ) : user.subscription?.status === 'active' ? (
                  <span className="text-green-600">{t('organizer.statusActive')}</span>
                ) : (
                  <span className="text-gray-600">{t('organizer.statusInactive')}</span>
                )}
              </h3>
            </div>
          </div>
          {/* Date end subscription period */}
          {subscriptionPeriodEnd && (
            <div className="px-3 bg-base-200 dark:bg-base-700 rounded-lg mb-0">
              <h3 className="text-sm font-semibold mb-1">{t('organizer.renewDate')+' '+subscriptionPeriodEnd}</h3>
            </div>
          )}

          {/* Cancellation Info */}
          {isCanceled && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-600 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon icon={faCheck} className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-base font-bold text-blue-900 dark:text-blue-200 mb-2">
                    {t('organizer.cancellationConfirmed')}
                  </p>
                  {subscriptionEndDate ? (
                    <>
                      <p className="text-sm text-blue-800 dark:text-blue-300 mb-1">
                        {t('organizer.continueAccessUntil', { date: subscriptionEndDate })}
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
                        {t('organizer.autoDowngradeToFree')}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      {t('organizer.accessUntilEndOfPeriod')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Subscription Expiration - Only show if canceled */}
          {subscriptionEndDate && currentPlan !== SubscriptionType.FREE && isCanceled && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
              <p className="text-sm font-semibold mb-1">{t('organizer.subscriptionExpires')}</p>
              <p className="text-lg text-blue-600 dark:text-blue-400">{subscriptionEndDate}</p>
            </div>
          )}

          {/* Plan Features */}
          <div className="mt-6 pt-4 border-t border-(--color-border)">
            <h3 className="font-semibold mb-3">{t('organizer.planFeatures')}</h3>
            <div className="space-y-2">
              {[
                {
                  label: t('organizer.featureEvents'),
                  value: planConfig.maxEvents,
                  unlimited: planConfig.maxEvents === Infinity,
                },
                {
                  label: t('organizer.featureSongsPerEvent'),
                  value: planConfig.maxSongsPerEvent,
                  unlimited: planConfig.maxSongsPerEvent === Infinity,
                },
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <FontAwesomeIcon
                    icon={feature.unlimited ? faCheck : faCheck}
                    className={`w-4 h-4 ${feature.unlimited ? 'text-green-600' : 'text-(--color-text-secondary)'}`}
                  />
                  <span>
                    {feature.label}:{' '}
                    <strong>
                      {feature.unlimited ? t('organizer.unlimited') : feature.value}
                    </strong>
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="mt-6 pt-4 border-t border-(--color-border) space-y-2">
            {currentPlan === SubscriptionType.FREE && (
              <button
                onClick={onNavigateToSubscription}
                className="btn btn-primary w-full!"
              >
                {t('organizer.upgradePlan')}
              </button>
            )}

            {currentPlan !== SubscriptionType.FREE && (
              <button
                onClick={onNavigateToSubscription}
                className="btn btn-outline w-full!"
              >
                {t('organizer.managePlan')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

